import { useMemo, useState } from "react";
import {
  Container,
  GroupContainer,
  InputWrapper,
  Label,
  Input,
  AddButton,
} from "./InputLinkGroupStyles";
import {
  InputLinkGroupProps,
  LinkItem,
} from "../../types/componentsConstructors";
import TrackCard from "../trackCard/TrackCard";
import YoutubeCover from "../../img/logged/youtubecover.png";
import SpotifyCover from "../../img/logged/spotifycover.png";
import SoundCloundCover from "../../img/logged/soundcloudcover.png";
import { shakeAnimation } from "../../styles/animations";
import { buildInstagramUrl, buildWhatsAppUrl } from "../../utils/socialLinks";
import { useGlobalLoading } from "../loading/GlobalLoading";

const isValidUrlFormat = (url: string): boolean => {
  try {
    const u = new URL(url);
    return u.protocol === "https:";
  } catch {
    return false;
  }
};

const isAllowedDomain = (url: string, allowed?: string[]): boolean => {
  if (!allowed || allowed.length === 0) return true;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return allowed.some((d) => host === d || host.endsWith(`.${d}`));
  } catch {
    return false;
  }
};

const InputLinkGroup = ({
  onChange,
  nameLabel = "Nome",
  namePlaceholder = "Digite um nome",
  urlLabel = "Link",
  urlPlaceholder = "https://www.link.com",
  initialItems = [{ name: "", url: "" }],
  allowAdd = true,
  previewEnabled = true,
  urlRequired = true,
  allowedDomains,
  autoBuildUrl,
}: InputLinkGroupProps & {
  urlRequired?: boolean;
  allowedDomains?: string[];
  autoBuildUrl?: "instagram" | "whatsapp";
}) => {
  const [groups, setGroups] = useState<LinkItem[]>(initialItems);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorIndex, setErrorIndex] = useState<number | null>(null);

  const { start, stop } = useGlobalLoading();

  const isMediaUrl = (url: string) =>
    /(?:youtu\.be|youtube\.com|soundcloud\.com|spotify\.com)/i.test(url);

  const fetchThumbnail = async (url: string): Promise<string | undefined> => {
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("watch?v=")
        ? url.split("watch?v=")[1].split("&")[0]
        : url.split("/").pop();
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }

    if (url.includes("soundcloud.com")) {
      try {
        const response = await fetch(
          `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(
            url
          )}`
        );
        const data = await response.json();
        if (data?.thumbnail_url) return data.thumbnail_url;
        return SoundCloundCover;
      } catch (e) {
        console.error("Erro ao buscar imagem SoundCloud", e);
        return SoundCloundCover;
      }
    }

    if (url.includes("spotify.com")) {
      return SpotifyCover;
    }

    return YoutubeCover;
  };

  const handleInputChange = async (
    index: number,
    field: keyof LinkItem,
    value: string
  ) => {
    const updated = [...groups];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "url" && value) {
      const image = await fetchThumbnail(value);
      updated[index].imageUrl = image;
    }

    setGroups(updated);
    onChange?.(updated);
  };

  const handleNameBlur = async (index: number) => {
    if (!autoBuildUrl) return;

    const nameValue = groups[index]?.name?.trim();
    if (!nameValue) return;

    start();
    await new Promise((r) => setTimeout(r, 400));

    let result = { ok: false, url: "", reason: "" };
    if (autoBuildUrl === "instagram") {
      result = buildInstagramUrl(nameValue) as any;
    }
    if (autoBuildUrl === "whatsapp") {
      result = buildWhatsAppUrl(nameValue) as any;
    }

    stop();

    if (result.ok && result.url) {
      await handleInputChange(index, "url", result.url);
    } else {
      console.warn("Erro ao gerar link:", result.reason);
    }
  };

  const handleAddGroup = () => {
    const lastIndex = groups.length - 1;
    const last = groups[lastIndex];

    const hasName = Boolean(last?.name?.trim());
    const hasUrl = Boolean(last?.url?.trim());

    const isValid = urlRequired ? hasName && hasUrl : hasName;

    if (!isValid) {
      setErrorIndex(lastIndex);
      window.setTimeout(() => setErrorIndex(null), 600);
      return;
    }

    const updated = [...groups, { name: "", url: "", imageUrl: "" }];
    setGroups(updated);
    onChange?.(updated);
  };

  const handlePlay = (index: number) => {
    setCurrentIndex(index);
    setIsPlaying(true);
  };

  const handlePause = () => setIsPlaying(false);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % groups.length);
    setIsPlaying(true);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? groups.length - 1 : prev - 1));
    setIsPlaying(true);
  };

  const perItemValidation = useMemo(() => {
    return groups.map((g) => {
      const name = g.name?.trim() ?? "";
      const url = g.url?.trim() ?? "";
      const urlEmpty = url === "";
      const urlFormatOk = urlEmpty ? !urlRequired : isValidUrlFormat(url);
      const urlDomainOk =
        urlEmpty && !urlRequired ? true : isAllowedDomain(url, allowedDomains);

      const showUrlError =
        url.length > 0 &&
        (!urlFormatOk || !urlDomainOk || !isValidUrlFormat(url));

      return {
        nameEmpty: name === "",
        urlEmpty,
        showUrlError,
      };
    });
  }, [groups, urlRequired, allowedDomains]);

  return (
    <Container>
      {groups.map((group, index) => {
        const showError = errorIndex === index;
        const { nameEmpty, showUrlError } = perItemValidation[index];

        return (
          <GroupContainer
            key={index}
            $error={showError}
            animate={showError ? shakeAnimation : {}}
          >
            <InputWrapper>
              <Label $error={showError && nameEmpty}>{nameLabel}:</Label>
              <Input
                $error={showError && nameEmpty}
                type="text"
                placeholder={namePlaceholder}
                value={group.name}
                onChange={(e) =>
                  handleInputChange(index, "name", e.target.value)
                }
                onBlur={() => handleNameBlur(index)} // ⬅️ auto-build
                inputMode="text"
              />
            </InputWrapper>

            <InputWrapper>
              <Label $error={showUrlError}>
                {urlLabel}
                {!urlRequired}:
              </Label>
              <Input
                $error={showUrlError}
                type="url"
                placeholder={urlPlaceholder}
                value={group.url}
                onChange={(e) =>
                  handleInputChange(index, "url", e.target.value)
                }
                inputMode="url"
              />
            </InputWrapper>

            {previewEnabled && group.url && isMediaUrl(group.url) && (
              <TrackCard
                index={index}
                currentIndex={currentIndex}
                isPlaying={isPlaying}
                onPlay={() => handlePlay(index)}
                onPause={handlePause}
                onNext={handleNext}
                onPrev={handlePrev}
                name={group.name}
                url={group.url}
                imageUrl={group.imageUrl}
              />
            )}
          </GroupContainer>
        );
      })}

      {allowAdd && <AddButton onClick={handleAddGroup}>+</AddButton>}
    </Container>
  );
};

export default InputLinkGroup;
